// Customer-facing strings for the Axaris-assessed NIST CSF 2.0 Implementation Tier.
//
// Extracted from GrcComplianceEstimate so the modal, the CSV and the readiness-bar tooltip all
// render one implementation (the securityInsightsRowText pattern), and so the strings can be
// tested without mounting the component. Every helper tolerates an older backend (field absent)
// and a not-assessed result; no "Tier null" or "Tier undefined" can render.
//
// A tier is the minimum over eight characteristics, so an unassessed characteristic only changes
// the answer when the minimum of the assessed ones is above 1. The backend therefore reports a
// status of determined / bounded / not_assessed, and these helpers render each distinctly.
//
// Customer-facing text carries no thresholds, credit values or indicator counts; the only numbers
// are the response-coverage percentage and the assessed-characteristic count.

export const NOT_ASSESSED_FALLBACK = 'Not assessed — insufficient evidence';
export const NOT_ASSESSED_CELL = 'Not assessed';

/** A component or characteristic cell: determined label, else the bounded range, else "Not assessed". */
export const tierCellText = (label, status, lower, upper) => {
    if (label != null) return label;
    if (status === 'bounded' && lower != null && upper != null) return `Tier ${lower}–${upper}`;
    return NOT_ASSESSED_CELL;
};

/**
 * The headline ("Current Overall Tier"). Leads with "not determined" for a bounded result deliberately:
 * a bare "Tier 1–2" would read as an official tier range, and NIST defines no such thing.
 */
export const tierHeadlineText = (tier) => {
    if (!tier) return NOT_ASSESSED_FALLBACK;
    if (tier.overallTierLabel != null) return tier.overallTierLabel;
    if (tier.overallTierStatus === 'bounded' && tier.overallTierLowerBound != null && tier.overallTierUpperBound != null) {
        return `Tier not determined — possible range Tier ${tier.overallTierLowerBound}–${tier.overallTierUpperBound}`;
    }
    return tier.notAssessedLabel ?? NOT_ASSESSED_FALLBACK;
};

/** Governance / Management stat cell. */
export const componentCellText = (tier, component) => {
    if (!tier) return NOT_ASSESSED_CELL;
    return tierCellText(
        tier[`${component}TierLabel`],
        tier[`${component}TierStatus`],
        tier[`${component}TierLowerBound`],
        tier[`${component}TierUpperBound`],
    );
};

/** One characteristic row in the modal's lists. */
export const characteristicCellText = (c) =>
    tierCellText(c?.levelLabel, c?.levelStatus, c?.levelLowerBound, c?.levelUpperBound);

// Matches the backend "0.#" invariant format (62.5 / 100 — never 62.50 or 100.0).
export const formatCoveragePct = (value) => {
    const n = Number(value ?? 0);
    if (!Number.isFinite(n)) return '0';
    return String(Math.round(n * 10) / 10);
};

/**
 * The "Next Tier to Consider" cell. The backend names the next tier (`nextTierLabel`, overall + 1)
 * whether or not it is assessable — the nuance lives in `nextTierNoteText` — so a Tier 2 that is
 * partially met reads "2 — Risk Informed", not "Tier 2 assessment unavailable". Older backends
 * without `nextTierLabel` fall through to the previous precedence.
 * Precedence: next-tier label → suggested label → unavailable reason → "No higher tier" at 4 → "—".
 */
export const suggestedNextText = (tier) => {
    if (!tier) return '—';
    if (tier.nextTierLabel != null) return tier.nextTierLabel;
    if (tier.suggestedNextTierLabel != null) return tier.suggestedNextTierLabel;
    if (tier.suggestedNextTierUnavailableReason != null) return tier.suggestedNextTierUnavailableReason;
    if (tier.overallTier === 4) return 'No higher tier';
    return '—';
};

/**
 * The line under the next-tier cell: the Tier 4 opt-out instruction, or how much of the next tier
 * the organisation already meets ("Partial coverage — 5 of 8 characteristics already meet Tier 2").
 * Null when there is no next tier or the backend predates the field.
 */
export const nextTierNoteText = (tier) => tier?.nextTierNote ?? null;

/** How many of the eight characteristics went unassessed; 0 when complete or unknown. */
const missingCount = (tier) => {
    const assessed = tier?.assessedCharacteristics;
    const total = tier?.totalCharacteristics;
    if (assessed == null || total == null || assessed >= total) return 0;
    return total - assessed;
};

/**
 * "Evidence confidence" — renamed from plain "Confidence" so it cannot be read as uncertainty
 * about a determined tier. A determined Tier 1 with unassessed characteristics is still Tier 1;
 * what is low is the completeness of the evidence behind it.
 *
 * The reason clause appears ONLY when incompleteness is the cause. Confidence is also forced Low
 * by thin response coverage alone, and saying a characteristic lacks evidence would then be false.
 */
export const confidenceText = (tier) => {
    const confidence = tier?.confidence ?? 'Low';
    const reason = tier?.tierConfidenceReason ?? confidenceReason(tier);
    return reason ? `Evidence confidence: ${confidence} — ${reason}` : `Evidence confidence: ${confidence}`;
};

export const confidenceReason = (tier) => {
    const missing = missingCount(tier);
    if (missing === 0) return null;
    return missing === 1
        ? 'one tier characteristic lacks sufficient evidence'
        : `${missing} tier characteristics lack sufficient evidence`;
};

/** The coverage percentage, split out so a Low confidence beside a high percentage no longer reads as self-contradictory. */
export const coverageText = (tier) =>
    `Response coverage: ${formatCoveragePct(tier?.evidenceCoveragePercent)}% of mapped indicators answered`;

/**
 * Why a determined tier survives unassessed characteristics. Without this the card reads as if it
 * were contradicting itself: a tier is a minimum, and the assessed characteristics already reach
 * the floor, so an unknown cannot move it.
 */
export const determinedNoteText = (tier) => {
    if (tier?.tierDeterminedNote != null) return tier.tierDeterminedNote;
    const missing = missingCount(tier);
    if (!missing || tier?.overallTier == null) return null;
    const subject = missing === 1
        ? 'The unassessed characteristic cannot'
        : `The ${missing} unassessed characteristics cannot`;
    return `${subject} change this Tier ${tier.overallTier} result — the assessed characteristics already establish it.`;
};

/**
 * The Gaps heading. Gaps are measured against the *next* tier, never the current one, so a bare
 * "Gaps" is under-specified. When the next tier is named but not assessable the list holds only
 * the characteristics still lacking evidence, so the heading says that — without calling the
 * tier "not assessable".
 */
export const gapsHeadingText = (tier) => {
    if (tier?.suggestedNextTierLabel != null) return `Gaps to Tier ${tier.suggestedNextTierLabel}`;
    if (tier?.overallTier === 4) return 'Gaps';
    if (tier?.nextTierAssessable === false && tier.nextTierLabel != null) return `Evidence needed for Tier ${tier.nextTierLabel}`;
    return 'Evidence needed';
};

/** One row of the Gaps list; an unassessed characteristic is called out as needing evidence. */
export const gapItemText = (c) =>
    c?.assessed === false
        ? `Evidence needed: ${c.name}`
        : `${c?.name} — ${characteristicSummaryText(c)}`;

/**
 * The expanded gap row: how many mapped indicators fall short, grouped by the tier each needs and
 * by whether that tier requires them. Says nothing about the questions behind them — no id, no
 * question text, no answer — because no question is stated outside the assessment itself (AX-200).
 * Unanswered indicators are counted separately: that is the difference between answering a
 * question and improving a practice.
 */
export const improvementGroupTexts = (c) => {
    const groups = [];
    for (const improvement of (c?.improvements || [])) {
        const requiredTier = improvement?.requiredTier;
        const required = !!improvement?.required;
        let group = groups.find(g => g.requiredTier === requiredTier && g.required === required);
        if (!group) {
            group = { requiredTier, required, count: 0, unanswered: 0 };
            groups.push(group);
        }
        group.count += 1;
        if (!improvement?.answer) group.unanswered += 1;
    }

    return groups
        .sort((a, b) => (a.requiredTier ?? 0) - (b.requiredTier ?? 0) || Number(b.required) - Number(a.required))
        .map(g => ({
            key: `tier-${g.requiredTier}-${g.required ? 'required' : 'optional'}`,
            required: g.required,
            text: `${g.count} ${g.count === 1 ? 'indicator needs' : 'indicators need'} Tier ${g.requiredTier}`
                + (g.unanswered > 0 ? ` (${g.unanswered} not answered)` : ''),
        }));
};

/** "6 of 8 tier characteristics assessed"; null when all eight were assessed (nothing to say). */
export const completenessText = (tier) => {
    if (!tier) return null;
    if (tier.tierCompletenessCardLine != null) return tier.tierCompletenessCardLine;
    const missing = missingCount(tier);
    if (!missing) return null;
    return `Characteristic completeness: ${tier.assessedCharacteristics} of ${tier.totalCharacteristics} assessed`;
};

/**
 * How one characteristic reads in the modal's "meets above" / "gaps" lists. Keeps the existing
 * "Tier 3 — Repeatable" phrasing for a determined level, and never doubles the word "Tier".
 */
export const characteristicSummaryText = (c) => {
    if (!c) return NOT_ASSESSED_FALLBACK;
    if (c.levelLabel != null) return `Tier ${c.levelLabel}`;
    if (c.levelStatus === 'bounded' && c.levelLowerBound != null && c.levelUpperBound != null) {
        return `Tier ${c.levelLowerBound}–${c.levelUpperBound} — not determined`;
    }
    return 'not assessed — insufficient evidence';
};

export const CSF_TIER_NAMES = ['Partial', 'Risk Informed', 'Repeatable', 'Adaptive'];

const TOTAL_CHARACTERISTICS = 8;

/**
 * The four tier-coverage rows behind the tier bar, one per Tier 1–4:
 * `{ tier, name, label, met, possible, total, notMeasured }` — how many of the eight
 * characteristics meet the tier, how many might (bounded), and whether Tier 4 is measured at all.
 * Taken from the backend's `tierCoverage`; against an older backend the rows are rebuilt from the
 * overall tier alone (all-or-nothing, as the old four-block bar was), so the bar never disappears.
 */
export const tierCoverageRows = (tier) => {
    const fromServer = Array.isArray(tier?.tierCoverage) && tier.tierCoverage.length === CSF_TIER_NAMES.length
        ? tier.tierCoverage
        : null;
    const total = Number(tier?.totalCharacteristics) > 0 ? Number(tier.totalCharacteristics) : TOTAL_CHARACTERISTICS;

    let metUpTo = 0;
    let possibleUpTo = 0;
    if (tier?.overallTier != null) {
        metUpTo = tier.overallTier;
    } else if (tier?.overallTierStatus === 'bounded' && tier.overallTierLowerBound != null && tier.overallTierUpperBound != null) {
        metUpTo = tier.overallTierLowerBound;
        possibleUpTo = tier.overallTierUpperBound;
    }

    return CSF_TIER_NAMES.map((name, i) => {
        const n = i + 1;
        const row = fromServer?.[i];
        if (row) {
            return {
                tier: row.tier ?? n,
                name: row.name ?? name,
                label: row.label ?? `${n} — ${name}`,
                met: Number(row.met) || 0,
                possible: Number(row.possible) || 0,
                total: Number(row.total) > 0 ? Number(row.total) : total,
                notMeasured: !!row.notMeasured,
            };
        }
        return {
            tier: n,
            name,
            label: `${n} — ${name}`,
            met: n <= metUpTo ? total : 0,
            possible: n > metUpTo && n <= possibleUpTo ? total : 0,
            total,
            notMeasured: false,
        };
    });
};

/**
 * One tier bar's state: "met" when every characteristic meets it, "partial" when some do (the
 * amber bar — the report used to show nothing here), "possible" when none is confirmed but a
 * bounded characteristic might, "not_measured" for an opted-out Tier 4, else "unmet".
 */
export const tierCoverageState = (row) => {
    if (!row || row.notMeasured) return row?.notMeasured ? 'not_measured' : 'unmet';
    if (row.met >= row.total) return 'met';
    if (row.met > 0) return 'partial';
    if (row.possible > 0) return 'possible';
    return 'unmet';
};

/** "5 of 8", or "Not measured" for an opted-out Tier 4. The number, not the colour, carries the value. */
export const tierCoverageCaption = (row) =>
    row?.notMeasured ? 'Not measured' : `${row?.met ?? 0} of ${row?.total ?? TOTAL_CHARACTERISTICS}`;

/**
 * One state per tier 1–4, as the old four-block bar reported them, now derived from the coverage
 * rows: a determined Tier N is "met" through N; a bounded result is "met" to its floor and
 * "possible" above it; partial coverage above the current tier reads "partial".
 */
export const tierBlockStates = (tier) => tierCoverageRows(tier).map(tierCoverageState);

// ── CSV rows for the tier status grid and the control mapping ─────────────────────────────────
// The CSV states the same tier explanation as the PDF: a status row per characteristic and the
// CSF 2.0 subcategories behind each characteristic's tiers. Every label is the server's, printed verbatim; nothing here
// derives a label, a credit or a status. The escape function is the caller's, so the CSV has one
// escaping rule.

const defaultCsvEscape = (field) => {
    const str = String(field ?? '');
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
};

const STATUS_TIERS = [2, 3, 4];

/**
 * One `Tier Status,<characteristic>,<Tier 2>,<Tier 3>,<Tier 4>` row per characteristic. Each
 * label is looked up by its step's `tier`, not by position, and a missing tier prints an empty
 * cell. A characteristic without `tierSteps` (an older backend) gets no row.
 */
export const buildCsfTierStatusCsvRows = (tier, escape = defaultCsvEscape) => {
    const rows = [];
    for (const c of (tier?.characteristics || [])) {
        const steps = Array.isArray(c?.tierSteps) ? c.tierSteps : [];
        if (steps.length === 0) continue;
        rows.push([
            'Tier Status',
            escape(c.name),
            ...STATUS_TIERS.map(t => escape(steps.find(s => s?.tier === t)?.label ?? '')),
        ].join(','));
    }
    return rows;
};

/**
 * One `Tier Mapping,<characteristic>,<component>,<tier>,<CSF 2.0 subcategories>` row per tier of
 * each characteristic's `controlMapping`, characteristics and tiers in array order. The mapping
 * names controls only: no question and no answer reaches any report (AX-200). Returns [] when no
 * characteristic carries a mapping (the popup's summary payload, or an older backend).
 */
export const buildCsfTierMappingCsvRows = (tier, escape = defaultCsvEscape) => {
    const rows = [];
    for (const c of (tier?.characteristics || [])) {
        for (const m of (Array.isArray(c?.controlMapping) ? c.controlMapping : [])) {
            rows.push([
                'Tier Mapping',
                escape(c.name),
                escape(c.axis),
                escape(m.tierLabel),
                escape(m.csfSubcategories ?? ''),
            ].join(','));
        }
    }
    return rows;
};
